<template>
  <v-menu
    v-model="open"
    bottom
    left
    offset-y
    :nudge-bottom="8"
    origin="top right"
    :min-width="width"
    :max-width="width"
    :max-height="maxHeight"
    content-class="app-menu app-hub-umenu"
  >
    <template v-slot:activator="{ on, attrs }">
      <button
        type="button"
        class="hub-utrigger"
        v-bind="attrs"
        aria-haspopup="menu"
        :aria-label="name ? 'Benutzermenü von ' + name : 'Benutzermenü'"
        v-on="on"
      >
        <app-avatar :name="name" :size="32" ring />
      </button>
    </template>

    <div class="app-hub-umenu__list" role="none">
      <!-- Kopf (nur Anzeige): Kachel mit Initialen, Name, Rolle, Mandant -->
      <div class="app-hub-umenu__head" role="none">
        <span class="app-hub-umenu__tile" aria-hidden="true">
          <template v-if="initials">{{ initials }}</template>
          <app-icon v-else name="user" :size="16" />
        </span>
        <span class="app-hub-umenu__who">
          <span class="app-hub-umenu__name" :title="name">{{ name }}</span>
          <span v-if="role" class="app-hub-umenu__meta">{{ role }}</span>
          <span v-if="customer" class="app-hub-umenu__meta" :title="customer">{{ customer }}</span>
        </span>
      </div>
      <div class="app-hub-umenu__sep" role="separator"></div>

      <div role="group" :aria-labelledby="ids.density">
        <div :id="ids.density" class="app-hub-umenu__label" role="presentation">Bedienung</div>
        <button
          v-for="option in densityOptions"
          :key="option.value"
          type="button"
          class="v-list-item app-menu__item app-hub-umenu__item app-hub-umenu__item--inset"
          role="menuitemradio"
          :aria-checked="option.value === wahl ? 'true' : 'false'"
          @click="chooseDensity(option.value)"
        >
          <span class="app-menu__text">{{ option.text }}</span>
          <app-icon v-if="option.value === wahl" name="check" :size="14" class="app-menu__check" />
        </button>
      </div>
      <div class="app-hub-umenu__sep" role="separator"></div>

      <div role="group" :aria-labelledby="ids.variant">
        <div :id="ids.variant" class="app-hub-umenu__label" role="presentation">Darstellung</div>
        <button
          v-for="option in variantOptions"
          :key="option.value"
          type="button"
          class="v-list-item app-menu__item app-hub-umenu__item app-hub-umenu__item--inset"
          role="menuitemradio"
          :aria-checked="option.value === variant ? 'true' : 'false'"
          @click="chooseVariant(option.value)"
        >
          <span class="app-menu__text">{{ option.text }}</span>
          <app-icon v-if="option.value === variant" name="check" :size="14" class="app-menu__check" />
        </button>
      </div>
      <div class="app-hub-umenu__sep" role="separator"></div>

      <router-link to="/settings/profile" class="v-list-item app-menu__item app-hub-umenu__item" role="menuitem">
        <app-icon name="user" :size="16" class="app-hub-umenu__icon" />
        <span class="app-menu__text">Mein Profil</span>
      </router-link>
      <a
        :href="docsUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="v-list-item app-menu__item app-hub-umenu__item"
        role="menuitem"
      >
        <app-icon name="bookOpen" :size="16" class="app-hub-umenu__icon" />
        <span class="app-menu__text">Kundendokumentation<span class="app-sr-only"> (öffnet in neuem Tab)</span></span>
        <app-icon name="external" :size="14" class="app-hub-umenu__trail" />
      </a>
      <button
        v-if="canSwitchTenant"
        type="button"
        class="v-list-item app-menu__item app-hub-umenu__item"
        role="menuitem"
        @click="switchTenant"
      >
        <app-icon name="building" :size="16" class="app-hub-umenu__icon" />
        <span class="app-menu__text">Mandant wechseln …</span>
      </button>
      <!-- unter 640 px ist die Kerbe (Produktwechsel) ausgeblendet -->
      <a v-if="narrow" :href="mobilityHubUrl" class="v-list-item app-menu__item app-hub-umenu__item" role="menuitem">
        <img src="/img/sima-star.png" alt="" width="16" height="16" class="app-hub-umenu__star" />
        <span class="app-menu__text">Zu Mobility Hub wechseln</span>
      </a>
      <div class="app-hub-umenu__sep" role="separator"></div>

      <button
        type="button"
        class="v-list-item app-menu__item app-hub-umenu__item app-hub-umenu__item--logout"
        role="menuitem"
        @click="$emit('sign-out')"
      >
        <app-icon name="logout" :size="16" class="app-hub-umenu__icon" />
        <span class="app-menu__text">Abmelden</span>
      </button>
    </div>
  </v-menu>
</template>

<script>
// Benutzermenü der Hub-Shell (Kopfleiste rechts, Aufbau und Maße wie das Benutzermenü des Mobility Hub, Farben aus
// tokens.css; Analyse „userMenu“ vom 10.10.2026).
// Knopf: rund 32 × 32 mit AppAvatar 32 (Initialen, Ring); Hover, gedrückt und offen 2 px Ring --sheet außen um den
// Avatar-Ring (wie im Hub: Rand 1 px plus ring-2), Fokus 2 px --link mit Abstand 2. Bei Touch-Dichte bleibt der Knopf
// sichtbar 32 groß, die Tippfläche wächst unsichtbar auf 44 × 44 (::before).
// Menü (v-menu bottom left offset-y): rechtsbündig unter dem Knopf, Abstand 8, Breite 240 (Touch 264), Radius 10,
// Polster 4, Kante --line, Schatten --sh-menu (Fläche, Kante, Schatten und Einträge aus app-menu in
// bedienelemente.css, hier nur die Abweichungen im Hub-Maß). Höhe höchstens bis 16 px über dem Fensterrand (scrollt).
// Inhalt: Kopf (Kachel 32 mit Radius 10 und Initialen weiß auf --acc · Name 14 600 · Rolle 12 · Mandant 12, beide
// --ink-3, gekürzt) · Bedienung (Automatisch/Maus/Touch → $dichte.setzeWahl) · Darstellung (Klassisch/Mobility Hub →
// $shell.setVariant), beide als menuitemradio mit Haken --link rechts · Mein Profil · Kundendokumentation (neuer Tab) ·
// optional „Mandant wechseln …“ (Prop with-tenant-switch, alle angemeldeten Benutzer; öffnet den Dialog über
// $shell.tenantSwitch) · unter 640 px „Zu Mobility Hub wechseln“ (dort fehlt die Kerbe) · Abmelden (Ereignis sign-out,
// rot wie im klassischen Benutzermenü).
// Einträge 32 hoch (Touch --h-menu 48), 14/20, Polster 0 8, Abstand 8, Radius --r-ctl, Icons 16 --ink-3; die Einträge
// der Auswahlgruppen sind um die Icon-Spalte eingerückt, damit alle Texte fluchten. Einträge tragen v-list-item für die
// Pfeiltasten von v-menu (Enter wählt, Esc schließt).
import AppIcon from '../ui/AppIcon.vue';
import AppAvatar, { initialsOf } from '../ui/AppAvatar.vue';
import {
  DOCS_URL,
  MOBILITY_HUB_URL,
  roleLabel,
  userDisplayName,
  customerDisplayName,
  tenantSwitchAllowed
} from '../../shell/navigation';
import { notePopup } from '../../shell/shellState';

// Fensterhöhe ohne Adressleiste (iOS), wo der Browser dvh kennt
const VH = typeof window !== 'undefined' && window.CSS && window.CSS.supports && window.CSS.supports('height', '100dvh')
  ? 'dvh'
  : 'vh';

export default {
  name: 'AppHubUserMenu',
  components: { AppIcon, AppAvatar },
  props: {
    // „Mandant wechseln …“ zusätzlich im Menü, z. B. wenn der Mandantenschalter der Kopfleiste fehlt
    withTenantSwitch: { type: Boolean, default: false }
  },
  data() {
    return {
      open: false,
      docsUrl: DOCS_URL,
      mobilityHubUrl: MOBILITY_HUB_URL,
      densityOptions: [
        { value: 'auto', text: 'Automatisch' },
        { value: 'maus', text: 'Maus' },
        { value: 'touch', text: 'Touch' }
      ],
      variantOptions: [
        { value: 'klassisch', text: 'Klassisch' },
        { value: 'hub', text: 'Mobility Hub' }
      ]
    };
  },
  computed: {
    name() {
      return userDisplayName(this.$store.state.profile);
    },
    initials() {
      return initialsOf(this.name);
    },
    role() {
      const profile = this.$store.state.profile;
      return roleLabel(profile ? profile.role : null);
    },
    customer() {
      return customerDisplayName(this.$store.state);
    },
    canSwitchTenant() {
      return this.withTenantSwitch && tenantSwitchAllowed(this.$store.state);
    },
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    wahl() {
      return this.$dichte ? this.$dichte.wahl : 'auto';
    },
    variant() {
      return this.$shell.variant;
    },
    // unter 640 px blendet die Kopfleiste die Kerbe aus; der Produktwechsel steht dann hier
    narrow() {
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : window.innerWidth;
      return width < 640;
    },
    width() {
      return this.touch ? 264 : 240;
    },
    // Menü beginnt etwa an der Unterkante der Kopfleiste (--hub-top 48/56, gesetzt an .shell--hub; als installierte
    // App zusätzlich die Safe Area oben)
    maxHeight() {
      return 'calc(100' + VH + ' - var(--hub-top, 48px) - env(safe-area-inset-top, 0px) - 16px)';
    },
    ids() {
      const base = 'app-hub-umenu-' + this._uid;
      return { density: base + '-bedienung', variant: base + '-darstellung' };
    }
  },
  watch: {
    open(value) {
      this.trackPopup(value);
    }
  },
  created() {
    // nicht reaktiv: ob dieses Menü gerade als offenes Popup gezählt ist (hält shell.popupOffen ausgeglichen, auch
    // wenn die Shell beim Wechsel der Darstellung mit offenem Menü abgebaut wird)
    this.popupNoted = false;
  },
  beforeDestroy() {
    this.trackPopup(false);
  },
  methods: {
    trackPopup(value) {
      if (value !== this.popupNoted) {
        this.popupNoted = value;
        notePopup(value);
      }
    },
    chooseDensity(value) {
      if (this.$dichte) {
        this.$dichte.setzeWahl(value);
      }
    },
    chooseVariant(value) {
      if (value !== this.$shell.variant) {
        this.$shell.setVariant(value);
      }
    },
    switchTenant() {
      this.open = false;
      this.$shell.tenantSwitch = true;
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------- Knopf */
.hub-utrigger {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  font-family: var(--font);
  cursor: pointer;
  transition: box-shadow .15s;
  -webkit-tap-highlight-color: transparent;
}
/* 1 px Avatar-Ring + 2 px Hover-Ring = 3 px --sheet; offen bleibt der Ring stehen, gedrückt ist die Rückmeldung bei
   Touch */
:where(html:not(.dichte-touch)) .hub-utrigger:hover,
.hub-utrigger:active,
.hub-utrigger[aria-expanded="true"] {
  box-shadow: 0 0 0 3px var(--sheet);
}
.hub-utrigger:focus {
  outline: none;
}
.hub-utrigger:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
/* Touch: Tippfläche 44 × 44 ohne die Kopfleiste zu verändern */
html.dichte-touch .hub-utrigger::before {
  content: '';
  position: absolute;
  top: -6px;
  right: -6px;
  bottom: -6px;
  left: -6px;
  border-radius: 50%;
}

/* ---------------------------------------------------------------- Menü (hängt am [data-app]; v-menu gibt der Fläche
   das Scope-Attribut dieser Komponente mit) */
.v-menu__content.app-hub-umenu {
  padding: 4px;
  border-radius: 10px;
}
.app-hub-umenu__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
}
.app-hub-umenu__tile {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--acc);
  color: #fff;
  font-size: 13px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: .02em;
  user-select: none;
}
.app-hub-umenu__who {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.app-hub-umenu__name,
.app-hub-umenu__meta {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.app-hub-umenu__name {
  font-size: 14px;
  line-height: 18px;
  font-weight: 600;
  color: var(--ink);
}
.app-hub-umenu__meta {
  font-size: 12px;
  line-height: 16px;
  color: var(--ink-3);
}
/* Trennlinie über die volle Breite (hebt das Polster 4 auf) */
.app-hub-umenu__sep {
  height: 1px;
  margin: 4px -4px;
  background: var(--line);
}
/* Gruppentitel auf der Icon-Spalte */
.app-hub-umenu__label {
  padding: 6px 8px 2px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--ink-3);
}

/* Einträge: Grundwerte, Hover --hov, gedrückt --press, Tastatur- und Fokusring --link und Haken --link aus app-menu */
.app-menu .app-hub-umenu__item {
  justify-content: flex-start;
  padding: 0 8px;
  border-radius: var(--r-ctl);
  font-size: 14px;
  line-height: 20px;
  text-decoration: none;
}
/* Auswahleinträge ohne Icon: Text auf der Linie der Icon-Einträge (8 + 16 + 8) */
.app-menu .app-hub-umenu__item--inset {
  padding-left: 32px;
}
.app-menu .app-hub-umenu__item .app-menu__text {
  flex: 1 1 auto;
}
.app-menu .app-hub-umenu__icon {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--ink-3);
}
.app-menu .app-hub-umenu__trail {
  flex: none;
  width: 14px;
  height: 14px;
  color: var(--ink-3);
}
.app-menu .app-hub-umenu__star {
  flex: none;
  display: block;
  width: 16px;
  height: 16px;
}
.app-menu .app-hub-umenu__item--logout,
.app-menu .app-hub-umenu__item--logout .app-hub-umenu__icon {
  color: var(--red);
}
.app-menu .app-hub-umenu__item--logout {
  font-weight: 500;
}

@media (prefers-reduced-motion: reduce) {
  .hub-utrigger {
    transition: none;
  }
}
</style>
